import {
	LitElement,
	html,
	ifDefined,
	styles,
	defineElement,
	generateUUID
} from "@mds/core";
import { MdsButton } from "@mds/button";
import { alertCSS } from "./alert.styles.js";

/**
 * Layout Web Component
 * @slot - This element has a slot
 */
export class MdsAlert extends LitElement {
	static get styles() {
		return [...styles, alertCSS];
	}

	static get properties() {
		return {
			open: { type: Boolean, reflect: true },
			id: { type: String },
			context: { type: String }, // success|info|warning|error
			title: { type: String },
			message: { type: String },
			actionText: { type: String, attribute: "action-text" },
			actionHref: { type: String, attribute: "action-href" },
			noclose: { type: Boolean }
		};
	}

	constructor() {
		super();

		this.open = false;
		this.title = undefined;
		this.id = generateUUID();
		this.message = undefined;
		this.actionText = undefined;
		this.actionHref = undefined;
		this.noclose = false;

		this.isError = false;
		this.titleId = generateUUID();
		this.descId = undefined;
		this.alert = undefined;
	}

	connectedCallback() {
		super.connectedCallback();

		this.isError = this.context === "error";
		this.descId = this.message ? generateUUID() : undefined;
	}

	firstUpdated(changedProperties) {
		super.firstUpdated(changedProperties);

		this.__checkAttrs();

		this.alert = this.shadowRoot.querySelector(".mds-alert");

		if (this.open && this.isError) {
			this.show();
		}
	}

	render() {
		if (this.isError) {
			return html`
				<dialog
					class="mds-alert ${this.context}"
					aria-labelledby="${this.titleId}"
					aria-describedby="${ifDefined(this.descId)}"
				>
					${this.__renderBody()}
				</dialog>
			`;
		} else {
			return html`
				<div
					class="mds-alert ${this.context}"
					role="dialog"
					aria-labelledby="${this.titleId}"
					aria-describedby="${ifDefined(this.descId)}"
					?data-open="${this.open}"
				>
					${this.__renderBody()}
				</div>
			`;
		}
	}

	show() {
		if (this.isError) {
			this.alert.show();
		} else {
			this.open = true;
		}
	}

	close() {
		if (this.isError) {
			this.alert.close();
		} else {
			this.open = false;
		}
	}

	__renderBody() {
		return html`
			<header>
				<h5 class="title" id="${this.titleId}">
					${this.title}
				</h5>
				${this.__createClose()}
			</header>
			<main>
				${this.__getMessage()} ${this.__createAction()}
			</main>
		`;
	}

	__getMessage() {
		if (!this.message) {
			return html``;
		} else {
			return html`
				<p id="${ifDefined(this.descId)}">${this.message}</p>
			`;
		}
	}

	__createClose() {
		if (this.noclose) {
			return html``;
		}

		return html`
			<mds-button
				context="subtle"
				icon="close"
				position="start"
				@click="${this.close}"
				aria-label="Close dialog"
			></mds-button>
		`;
	}

	__createAction() {
		if (!this.actionText) {
			return html``;
		}

		if (this.actionHref) {
			return html`
				<mds-button
					type="link"
					context="subtle"
					href="${this.actionHref}"
					?autofocus="${this.isError}"
					aria-labelledby="${this.titleId}"
				>
					<span slot="text">${this.actionText}</span>
				</mds-button>
			`;
		} else {
			return html`
				<mds-button
					context="subtle"
					@click="${this.__handleButtonAction}"
					?autofocus="${this.isError}"
					aria-labelledby="${this.titleId}"
				>
					<span slot="text">${this.actionText}</span>
				</mds-button>
			`;
		}
	}

	__handleButtonAction() {
		const event = new CustomEvent("action", {
			bubbles: true,
			cancelable: true
		});
		this.dispatchEvent(event);
	}

	__checkAttrs() {
		if (!this.title) {
			console.error("Required attribute `title` not given.");
			return html``;
		}

		if (!/success|info|warning|error/i.test(this.context)) {
			console.error("Invalid context value given.");
			return html``;
		}
	}
}

defineElement("mds-alert", MdsAlert);
